import { Box, ClickAwayListener } from "@mui/material";
import { type FC, useCallback, useState } from "react";

import { nameByPlatform } from "~/constants/platfroms";
import { MarketGroupsMenu } from "~/feature/chart/components/MarketGroupsMenu";
import {
  buildDropdownGroups,
  type IMarketDropdownGroup,
} from "~/feature/chart/components/MarketsDropdown/components/MarketsDropdownMenu/buildDropdownGroups";
import {
  ArrowIcon,
  DropdownPaper,
} from "~/feature/chart/components/MarketsDropdown/MarketsDropdown.styles";
import type { IXShareSelection } from "~/feature/chart/components/XShareDialog/types";
import { colorByPlatform } from "~/feature/chart/constants";
import type { IMarket } from "~/types/event";

import { CONSENSUS_LABEL } from "./constants";
import { getMenuScrollTop } from "./scrollMenuToItem";
import * as Styled from "./XShareMarketPicker.styles";

interface IXShareMarketPickerProps {
  marketGroups: Map<string, IMarket[]>;
  value: IXShareSelection | null;
  onChange: (selection: IXShareSelection) => void;
}

const getSelectedIds = (
  marketGroups: Map<string, IMarket[]>,
  value: IXShareSelection | null
): string[] => {
  if (!value) return [];
  if (value.marketId) return [value.marketId];

  return (marketGroups.get(value.groupId) ?? []).map(({ id }) => id);
};

export const XShareMarketPicker: FC<IXShareMarketPickerProps> = ({
  marketGroups,
  value,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(
    value?.groupId ?? null
  );
  const scrollToSelected = useCallback((menu: HTMLDivElement | null) => {
    const item = menu?.querySelector<HTMLElement>("[data-selected]");

    if (menu && item) {
      menu.scrollTop = getMenuScrollTop(
        menu.getBoundingClientRect(),
        item.getBoundingClientRect(),
        menu.scrollTop
      );
    }
  }, []);

  const groups = buildDropdownGroups(
    marketGroups,
    getSelectedIds(marketGroups, value),
    expandedGroupId
  );
  const selectedGroup = value ? marketGroups.get(value.groupId) : undefined;
  const selectedMarket = selectedGroup?.find(
    ({ id }) => id === value?.marketId
  );
  const label = selectedGroup
    ? `${selectedGroup[0].title ?? ""} · ${
        selectedMarket
          ? nameByPlatform[selectedMarket.platform]
          : CONSENSUS_LABEL
      }`
    : "";

  const select = (selection: IXShareSelection) => {
    onChange(selection);
    setIsOpen(false);
  };

  const handleGroupClick = ({
    id,
    isExpandable,
    markets,
  }: IMarketDropdownGroup) => {
    if (isExpandable) {
      setExpandedGroupId((previousId) => (previousId === id ? null : id));
      return;
    }

    select({ groupId: id, marketId: markets[0].id });
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box
        ref={setAnchorEl}
        onKeyDown={(event) => {
          if (isOpen && event.key === "Escape") {
            event.stopPropagation();
            setIsOpen(false);
          }
        }}
      >
        <Styled.Trigger
          onClick={() => setIsOpen((previous) => !previous)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <Styled.Dot
            $color={selectedMarket && colorByPlatform[selectedMarket.platform]}
          />
          <Styled.Label>{label}</Styled.Label>
          <ArrowIcon $open={isOpen} />
        </Styled.Trigger>

        <Styled.Menu open={isOpen} anchorEl={anchorEl} placement="bottom-start">
          <DropdownPaper>
            <MarketGroupsMenu
              ref={scrollToSelected}
              groups={groups}
              selectedIds={value?.marketId ? [value.marketId] : []}
              isConsensusSelected={({ id }) =>
                value?.groupId === id && value.marketId === null
              }
              onGroupClick={handleGroupClick}
              onMarketClick={({ id }, marketId) =>
                select({ groupId: id, marketId })
              }
              onConsensusClick={({ id }) =>
                select({ groupId: id, marketId: null })
              }
            />
          </DropdownPaper>
        </Styled.Menu>
      </Box>
    </ClickAwayListener>
  );
};
